/**
 * What the list should be offering you, given what's in the house.
 *
 * Two sources, kept apart because they answer different questions:
 *
 * - **Recipe gap** — "I want to cook this, what am I missing?" The strongest,
 *   because it follows a decision you already made.
 * - **Running low** — countable things down to their last one, and things that
 *   have run out entirely since you last shopped.
 *
 * The third source, typing something in, needs no computing.
 *
 * None of this could be trusted before ingredient synonyms and assumed staples
 * existed. A suggestion to buy salt every week, or an eggplant while an
 * aubergine sits in the fridge, is worse than an empty list — it is a list
 * nobody reads, and a wrong "you're missing this" gets acted on in a shop.
 */

import { missingIngredients } from "../recipes/matchIngredients";
import type { Suggestion } from "../recipes/suggestRecipes";
import type { FoodItem } from "../food/types";
import type { CartLine } from "./types";

/** Countable units. A weight at 1 means one gram, which means nothing. */
const COUNTABLE = new Set(["pcs", "pack"]);

/** How many recipes' gaps to offer. More than this stops being a list. */
const MAX_FROM_RECIPES = 6;

export interface CartSuggestion {
  name: string;
  source: "recipe" | "low";
  reason?: string;
}

/**
 * The ingredients standing between you and the recipes you'd actually cook.
 *
 * Takes the already-ranked suggestions rather than recomputing, so the list is
 * offering gaps in the same order the recipe screen would — what's about to
 * spoil first. A recipe you have everything for contributes nothing, which is
 * correct: there is nothing to buy.
 */
export function gapsFromRecipes(suggestions: Suggestion[]): CartSuggestion[] {
  const seen = new Set<string>();
  const out: CartSuggestion[] = [];

  for (const suggestion of suggestions.slice(0, MAX_FROM_RECIPES)) {
    for (const ingredient of suggestion.missing) {
      const key = ingredient.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ name: ingredient, source: "recipe", reason: suggestion.recipe.title });
    }
  }

  return out;
}

/**
 * Things down to the last one.
 *
 * Only countable units. "1 kg of rice" is a full bag and "1 pcs of eggs" is one
 * egg, and treating those the same would put rice on the list every week — the
 * exact failure the staples list exists to avoid, arriving by another door.
 */
export function runningLow(items: FoodItem[]): CartSuggestion[] {
  return items
    .filter((item) => COUNTABLE.has(item.unit) && item.quantity <= 1)
    .map((item) => ({ name: item.name, source: "low" as const, reason: "Last one" }));
}

/**
 * Everything worth offering, minus what's already on the list.
 *
 * Compared by name rather than by id, because the same thing can be suggested
 * by two recipes and typed in by hand, and a list that shows "chorizo" three
 * times is one nobody trusts.
 */
export function suggestCart(
  suggestions: Suggestion[],
  items: FoodItem[],
  onList: CartLine[]
): CartSuggestion[] {
  const already = new Set(onList.map((line) => line.name.toLowerCase()));
  const seen = new Set<string>();

  return [...gapsFromRecipes(suggestions), ...runningLow(items)].filter((entry) => {
    const key = entry.name.toLowerCase();
    if (already.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** What a shop would leave you needing, given a recipe you've decided to cook. */
export function gapsForRecipe(items: FoodItem[], ingredients: string[]): string[] {
  return missingIngredients(items, ingredients);
}
